import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";

import { AlertBanner } from "@qubit/autoparts";
import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { ASDSThemeProvider } from "~/components/designSystem/ASDSThemeProvider";
import { AutostoreBin } from "~/features/autostoreBin";
import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import {
  selectNextEmptyBinByPort,
  selectIsShortSideBin,
  selectThisWorkstation
} from "~/redux/selectors/workstationsSelectors";

import {
  closeFoodSafetyModal,
  setBinOverrides
} from "./autostorePutaway.slice";

export const FoodSafetyConfigurationModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const trackedModalName = useMixpanelModalName("Food Safety");

  const ports = useAppSelector(selectThisWorkstation)?.ports ?? [];
  const binInfo = useAppSelector(selectNextEmptyBinByPort);
  const [selectedPorts, setSelectedPorts] = useState<number[]>([]);
  const isShortSideBin = useAppSelector(selectIsShortSideBin);

  const portClick = useCallback(
    (portId: number) => {
      const isSelectedPort = selectedPorts.includes(portId);
      mixpanelTrack({
        trackedPageName: trackedModalName,
        type: "Button Click",
        label: isSelectedPort ? "Deselect Port" : "Select Port",
        eventProperties: {
          PortId: portId
        }
      });
      if (isSelectedPort) {
        setSelectedPorts(selectedPorts.filter((p) => p != portId));
      } else {
        setSelectedPorts([...selectedPorts, portId]);
      }
    },
    // dont add analytics dependencies to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPorts]
  );

  const handleConfirm = () => {
    const nonWholeBinPorts = selectedPorts.filter(
      (portId) =>
        binInfo[portId]?.autostoreBinConfiguration?.numberOfCompartments !== 1
    );
    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Button Click",
      label: "Confirm",
      eventProperties: {
        "Selected Ports": selectedPorts,
        BinsSelectedToConvertToWholeBinCount: nonWholeBinPorts.length
      }
    });
    dispatch(setBinOverrides(nonWholeBinPorts));
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      onClose={() => dispatch(closeFoodSafetyModal())}
    >
      <DialogTitle>{t("food safety")}</DialogTitle>
      <Box sx={{ width: "fit-content", mt: 3, mx: "auto" }}>
        <ASDSThemeProvider>
          <AlertBanner severity="warning">
            {t("whole bins required for food safety items")}
          </AlertBanner>
        </ASDSThemeProvider>
      </Box>
      <DialogContent>
        <Typography variant="h4" textAlign="center" mt={2}>
          {t(
            "to ensure food safety, please designate one or more ports for whole bins"
          )}
        </Typography>
        <Stack
          direction="row"
          gap={2}
          py={2}
          justifyContent="center"
          component={"ul"}
        >
          {ports.map((p) => {
            const bin = binInfo[p.portId];
            if (!bin) {
              return;
            }

            const selected = selectedPorts.includes(p.portId);
            const binConfig = bin.autostoreBinConfiguration;

            return (
              <Box
                component="li"
                key={p.portId}
                width={isShortSideBin ? "auto" : "100%"}
                maxWidth="400px"
                border="12px solid"
                p={1}
                borderColor={selected ? "primary.main" : "transparent"}
                aria-selected={selected}
                borderRadius="8px"
                aria-label={`port ${p.portId}`}
                sx={{ listStyleType: "none", justifyItems: "center" }}
              >
                <AutostoreBin
                  state="Bin Opened"
                  numberOfColumns={binConfig?.verticalCompartmentCount ?? 1}
                  numberOfRows={binConfig?.horizontalCompartmentCount ?? 1}
                  pickCompartment={null}
                  pickQuantity={""}
                  onBinClick={() => portClick(p.portId)}
                  isShortSideBin={isShortSideBin}
                />
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", mb: 2 }}>
        <Button
          variant="subtle"
          size="large"
          onClick={() => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              type: "Button Click",
              label: "Cancel"
            });
            dispatch(closeFoodSafetyModal());
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          size="large"
          disabled={!selectedPorts.length}
          onClick={handleConfirm}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
